import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { Check, ChevronDown, X } from "lucide-react";
import "../input/style.css";
import "./uiSelect.css";

type Option = {
  label: string;
  value: string | number;
};

interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "onChange" | "value"> {
  label?: string;
  error?: string;
  options: Option[];
  placeholder?: string;
  allowClear?: boolean;
  onClear?: () => void;
  value?: string | number;
  /** Called with a change-event-shaped object (`e.target.value`), so callers
   * written against the old native <select> keep working unchanged. */
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

// Beyond this many options a filter box inside the panel earns its height.
const SEARCH_THRESHOLD = 8;
const PANEL_MAX_HEIGHT = 264;

type PanelPos = { left: number; width: number; top?: number; bottom?: number };

// Custom listbox (trigger + portalled panel) in the smart-solution app's
// select language: same box as the text input, rounded panel, blue hover
// row and a check on the selected one. The native <select> popup can't be
// styled, which is why this is not a plain <select>.
export default function Select({
  label,
  error,
  options,
  placeholder = "Select an option",
  allowClear = false,
  onClear,
  className,
  value,
  onChange,
  disabled,
  id,
  name,
  "aria-label": ariaLabel,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [pos, setPos] = useState<PanelPos | null>(null);

  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => String(o.value) === String(value ?? ""));
  const hasValue = value !== undefined && value !== null && String(value) !== "";
  const searchable = options.length > SEARCH_THRESHOLD;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => String(o.label).toLowerCase().includes(q)) : options;
  }, [options, query]);

  const place = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom;
    // Flip above when the panel would run off the bottom and there is room up top.
    if (below < PANEL_MAX_HEIGHT + 16 && rect.top > below) {
      setPos({ left: rect.left, width: rect.width, bottom: window.innerHeight - rect.top });
    } else {
      setPos({ left: rect.left, width: rect.width, top: rect.bottom });
    }
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
  }, []);

  const openPanel = () => {
    if (disabled) return;
    const i = options.findIndex((o) => String(o.value) === String(value ?? ""));
    setActiveIndex(i >= 0 ? i : 0);
    place();
    setOpen(true);
  };

  useLayoutEffect(() => {
    if (!open) return;
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      close();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, close]);

  // Keep the keyboard-active row scrolled into view.
  useEffect(() => {
    if (!open) return;
    panelRef.current
      ?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  const emit = (next: string) => {
    onChange?.({
      target: { value: next, name },
      currentTarget: { value: next, name },
    } as unknown as React.ChangeEvent<HTMLSelectElement>);
  };

  const choose = (opt: Option) => {
    emit(String(opt.value));
    close();
    triggerRef.current?.focus();
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClear) onClear();
    else emit("");
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openPanel();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      triggerRef.current?.focus();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (visible[activeIndex]) choose(visible[activeIndex]);
    } else if (e.key === "Tab") {
      close();
    }
  };

  return (
    <div className="uiSelect">
      {label && <label className="uiField-label">{label}</label>}

      <div
        ref={triggerRef}
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={ariaLabel ?? label ?? placeholder}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : 0}
        className={clsx(
          "uisel-trigger",
          open && "open",
          disabled && "disabled",
          error && "is-error",
          className,
        )}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onKeyDown}
      >
        <span className={clsx("uisel-value", !selected && "placeholder")}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="uisel-controls">
          {allowClear && hasValue && !disabled && (
            <button type="button" className="uisel-clear" onClick={clear} aria-label="Clear selection">
              <X size={12} strokeWidth={2.5} />
            </button>
          )}
          <ChevronDown size={16} strokeWidth={2} className="uisel-caret" />
        </span>
      </div>

      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="uisel-panel"
            role="listbox"
            style={{
              position: "fixed",
              left: pos.left,
              width: pos.width,
              top: pos.top,
              bottom: pos.bottom,
            }}
          >
            {searchable && (
              <input
                autoFocus
                className="uisel-search"
                placeholder="Search…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
                onKeyDown={onKeyDown}
              />
            )}
            {visible.length === 0 ? (
              <div className="uisel-empty">No options</div>
            ) : (
              visible.map((opt, i) => {
                const isSelected = String(opt.value) === String(value ?? "");
                return (
                  <div
                    key={opt.value}
                    data-index={i}
                    role="option"
                    aria-selected={isSelected}
                    className={clsx(
                      "uisel-option",
                      i === activeIndex && "active",
                      isSelected && "selected",
                    )}
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => choose(opt)}
                  >
                    <span className="uisel-optionText">{opt.label}</span>
                    {isSelected && <Check size={14} strokeWidth={2.5} className="uisel-checkIcon" />}
                  </div>
                );
              })
            )}
          </div>,
          document.body,
        )}

      {error && <p className="uiField-error">{error}</p>}
    </div>
  );
}
